import debug from "debug";
import * as THREE from "three";

const log = debug("app:image");

/**
 * Handles the rendering of CSS3D objects into SVG and overlays them onto a canvas.
 * @category Components
 * @subcategory TitleBlock
 */
export class CSSRendering {
  /**
   * Creates an instance of CSSRendering.
   * @param {Object} experience - The 3D experience containing the scene.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;  // ✅ Add event hub for global event handling
    this.originalCSSVisibility = new Map(); 

    this._bitmapCache = new Map();   // key -> {bitmap, w, h}
    this._raf = null;                // coalesce needsRender

    this.setupListeners();  // ✅ Listen for rendering updates
}

setupListeners() {
  this.eventHub.on("needsRender", (state) => {
    if (state !== "true") return;
      if (this._raf) return; // already scheduled
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.update();
      });
  });
}

update() {
  if (!this.scene || !this.experience.camera.instance) return;

  log("🔄 Updating CSS Rendering...");
  
  const cloneCamera = this.experience.camera.instance.clone();
  cloneCamera.updateProjectionMatrix();
  
  this.renderCSS({
      cloneCamera,
      canvas: this.experience.renderer.instance.domElement,
      context: this.experience.renderer.instance.domElement.getContext("2d"),
      sizes: this.experience.sizes,
      scaleFactor: 2  // Adjustable scale factor
  });
}

  // Find all CSS3D objects (those with userData.mode === "textbox")

  /**
   * Finds all CSS3D objects with `userData.mode === "textbox"` in the scene.
   * @returns {Array} An array of CSS3D objects.
   */
  extractCSS3DObjects() {
    const css3DObjects = [];
    this.scene.traverse((object) => {
      if (object.userData && object.userData.mode === "textbox") {
        css3DObjects.push(object);
      }
    });
    return css3DObjects;
  }


  // Project a world position relative to clonedCamera into 2D canvas coordinates

  /**
   * Projects a 3D world position into 2D canvas coordinates.
   * @param {THREE.Vector3} worldPos - The position in 3D world space.
   * @param {THREE.Camera} clonedCamera - The camera used for projection.
   * @param {number} canvasWidth - The width of the canvas.
   * @param {number} canvasHeight - The height of the canvas.
   * @returns {Object} The projected 2D coordinates with `x` and `y`.
   */
  projectToCanvasPosition(worldPos, clonedCamera, canvasWidth, canvasHeight) {
    const screenPos = worldPos.clone().project(clonedCamera);
    const canvasX = (screenPos.x * 0.5 + 0.5) * canvasWidth;
    const canvasY = (screenPos.y * -0.5 + 0.5) * canvasHeight;
    return { x: canvasX, y: canvasY };
  }

  // Create the SVG string with data from userData

  /**
   * Creates an SVG string representation of a text box using the userData.
   * @param {Object} userData - The user data containing text box properties.
   * @param {number} pixelToWorldRatio - The ratio of pixels to world units.
   * @param {THREE.Camera} cloneCamera - The camera instance.
   * @param {number} scaleFactor - The scaling factor for rendering quality.
   * @returns {string} The generated SVG string.
   */
  createSVGFromUserData(userData, pixelToWorldRatio, cloneCamera, scaleFactor) {
    const {
      text,
      fontSize,
      bgColor,
      textColor,
      w,
      h,
      fontFamily,
      bold,
      outlineColor,
    } = userData;

    log("createsvg - User data", userData);
    log("createsvg - pixeltoWorldRatio", pixelToWorldRatio);
    log("createsvg - Camera zoom", cloneCamera.zoom);
    log("createsvg - scaleFactor", scaleFactor);

    // Convert width and height from world units to pixel units using pixelToWorldRatio
    // Also factor in zoom and scaleFactor for higher quality rendering and correct appearance
    const scaledWidth = w * pixelToWorldRatio * cloneCamera.zoom * scaleFactor;
    const scaledHeight = h * pixelToWorldRatio * cloneCamera.zoom * scaleFactor;

    log("scaled sizes: w, h", scaledWidth, scaledHeight);

    // Prepare colors
    const bg = this.resolveRGB(userData.bgColor, { red: 255, green: 255, blue: 255 });
    const fg = this.resolveRGB(userData.textColor, { red: 0, green: 0, blue: 0 });

    const bgColorString   = `rgb(${bg.red},${bg.green},${bg.blue})`;
    const textColorString = `rgb(${fg.red},${fg.green},${fg.blue})`;

    const fontWeight = (userData.bold ? "bold" : "normal");

    log(
      "colors bgColor, textColor, fontWeigh",
      bgColor,
      textColorString,
      fontWeight
    );

    // Adjust the font size based on the zoom and scale factor
    const adjustedFontSize = fontSize * cloneCamera.zoom * scaleFactor;

    log("adjusted font size", adjustedFontSize);

    const borderStyle = outlineColor
      ? `stroke:rgb(${outlineColor.red},${outlineColor.green},${
          outlineColor.blue
        }); stroke-width:${3 * cloneCamera.zoom * scaleFactor}; fill-opacity:1;`
      : "fill-opacity:1;";
    // Split text into lines for wrapping
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const svgString = `
  <svg xmlns="http://www.w3.org/2000/svg" width="${scaledWidth}" height="${scaledHeight}" viewBox="0 0 ${scaledWidth} ${scaledHeight}">
    <rect width="100%" height="100%" fill="${bgColorString}" style="${borderStyle}" />
    <text x="0" y="${adjustedFontSize}" fill="${textColorString}" font-size="${adjustedFontSize}" font-family="${
      fontFamily || "Arial"
    }"
      font-weight="${fontWeight}">
      ${lines
        .map(
          (line, index) =>
            `<tspan x="0" dy="${
              index === 0 ? "0" : adjustedFontSize
            }">${line}</tspan>`
        )
        .join("")}
    </text>
  </svg>
  `;

    log("svgString", svgString);

    return svgString;
  }
  // Draw the generated SVG on the provided canvas

  /**
   * Draws an SVG string onto a canvas.
   * @param {string} svgString - The SVG string to draw.
   * @param {CanvasRenderingContext2D} ctx - The canvas 2D rendering context.
   * @param {number} x - The x-coordinate for the drawing.
   * @param {number} y - The y-coordinate for the drawing.
   * @param {number} width - The width of the drawing.
   * @param {number} height - The height of the drawing.
   * @param {number} angle - The rotation angle in degrees.
   * @returns {Promise<void>} A promise that resolves when the SVG is drawn.
   */
  // Build a stable key from properties that affect raster output
  _keyFromUserData(u, pxPerWorld, zoom, scaleFactor) {
    return JSON.stringify({
      t: u.text, fs: u.fontSize, ff: u.fontFamily, b: u.bold,
      bg: u.bgColor, fg: u.textColor, o: u.outlineColor,
      w: u.w, h: u.h, px: pxPerWorld, z: zoom, s: scaleFactor
    });
  }

  /**
   * Draw an SVG (as bitmap) on ctx, with caching and no <img> churn.
   */
  async drawSVGOnCanvas(svgString, ctx, x, y, width, height, angle, cacheKey) {
    if (!ctx || !width || !height) return;

    // 1) Lookup or create ImageBitmap
    let entry = this._bitmapCache.get(cacheKey);
    if (!entry) {
      const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });

      // Fast path: ImageBitmap (no DOM img, no onload, no URL)
      let bitmap = null;
      try {
        bitmap = await createImageBitmap(blob, { premultiplyAlpha: "premultiply", colorSpaceConversion: "default" });
      } catch {
        // Fallback for older browsers
        bitmap = await new Promise((resolve) => {
          const url = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => { resolve(img); URL.revokeObjectURL(url); };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
          img.src = url;
        });
      }
      if (!bitmap) return;

      entry = { bitmap, w: width, h: height };
      this._bitmapCache.set(cacheKey, entry);
    }

    // 2) Draw
    ctx.save();
    ctx.translate(x + width / 2, y + height / 2);
    ctx.rotate(-angle * Math.PI / 180);
    ctx.drawImage(entry.bitmap, -width / 2, -height / 2, width, height);
    ctx.restore();
  }

/** Call this when a textbox’s props change to drop stale bitmaps. */
invalidateTextboxCache() {
  for (const v of this._bitmapCache.values()) {
    try { v.bitmap.close?.(); } catch {}
  }
  this._bitmapCache.clear();
}


  // Main rendering method to handle CSS3D objects and SVG overlays

  
  /**
   * Renders all CSS3D objects as SVGs and overlays them onto the canvas.
   * @param {Object} params - The rendering parameters.
   * @param {THREE.Camera} params.cloneCamera - The camera instance for rendering.
   * @param {HTMLCanvasElement} params.canvas - The canvas element.
   * @param {CanvasRenderingContext2D} params.context - The canvas 2D context.
   * @param {Object} params.sizes - The size configuration for rendering.
   * @param {number} params.sizes.pixelToWorldRatio - Ratio of pixels to world units.
   * @param {number} params.scaleFactor - The scaling factor for quality.
   * @returns {Promise<void>} A promise that resolves when rendering is complete.
   */
  async renderCSS({ cloneCamera, canvas, context, sizes, scaleFactor }) {
    const W = canvas.width, H = canvas.height;
    const css3DObjects = this.extractCSS3DObjects();
    if (!context) return;

    for (const cssObject of css3DObjects) {
      const { position, userData, quaternion } = cssObject;
      const euler = new THREE.Euler().setFromQuaternion(quaternion);
      let angle = THREE.MathUtils.radToDeg(euler.y);
      angle = (Math.round(angle / 90) * 90 + 360) % 360;

      const { x: cx, y: cy } = this.projectToCanvasPosition(position, cloneCamera, W, H);

      const width  = userData.w * sizes.pixelToWorldRatio * cloneCamera.zoom * scaleFactor;
      const height = userData.h * sizes.pixelToWorldRatio * cloneCamera.zoom * scaleFactor;

      // Simple culling
      if ((cx + width/2) < 0 || (cy + height/2) < 0 || (cx - width/2) > W || (cy - height/2) > H) continue;

      const svgString = this.createSVGFromUserData(userData, sizes.pixelToWorldRatio, cloneCamera, scaleFactor);
      const cacheKey = this._keyFromUserData(userData, sizes.pixelToWorldRatio, cloneCamera.zoom, scaleFactor);

      await this.drawSVGOnCanvas(svgString, context, cx - width/2, cy - height/2, width, height, angle, cacheKey);
    }
  }



  resolveRGB(c, fallback = { red: 255, green: 255, blue: 255 }) {
    // accept {red,green,blue} or CSS strings like "#aabbcc" or "rgb(…)"
    if (typeof c === "string") {
      const m = c.trim().match(/^#?([0-9a-f]{6})$/i);
      if (m) {
        const n = parseInt(m[1], 16);
        return {
          red:   (n >> 16) & 255,
          green: (n >> 8)  & 255,
          blue:  n & 255,
        };
      }
    }
    const r = (c?.red   ?? fallback.red)   | 0;
    const g = (c?.green ?? fallback.green) | 0;
    const b = (c?.blue  ?? fallback.blue)  | 0;
    return { red: Math.max(0, Math.min(255, r)),
            green: Math.max(0, Math.min(255, g)),
            blue: Math.max(0, Math.min(255, b)) };
  }

  // Cleanup method to restore any visibility and remove references

  /**
   * Restores visibility of CSS objects and clears references.
   */
  cleanup() {
    log("Restoring original CSS visibility and cleaning up.");
    this.originalCSSVisibility.forEach((_, cssObject) => {
      this.showCSSDiv(cssObject); // Restore visibility of CSS objects
    });
  }
}
